"use client";

import * as React from "react";
import { Check, FileText, Trash2, UploadCloud, X } from "lucide-react";
import { upload } from "@vercel/blob/client";
import { RequiredMark } from "../required-mark";
import { cn } from "../utils";
type Status = "uploading" | "success" | "error";
const DEFAULT_MAX_SIZE_MB = 15;
const DEFAULT_ACCEPTED_MIME_TYPES = ["image/jpeg", "image/png", "image/svg+xml", "application/pdf"];
const EXTENSION_BY_MIME: Record<string, string> = {
  "image/jpeg": "JPG",
  "image/png": "PNG",
  "image/svg+xml": "SVG",
  "application/pdf": "PDF",
  "text/csv": "CSV",
  "application/vnd.ms-excel": "XLS",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "text/plain": "TXT"
};

// File extension for each accepted type. Used to widen the picker `accept` hint
// and to validate files whose browser-reported MIME type is unreliable (CSV and
// the Office formats vary by OS and browser).
const FILE_EXTENSION_BY_MIME: Record<string, string> = {
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/svg+xml": ".svg",
  "application/pdf": ".pdf",
  "text/csv": ".csv",
  "application/vnd.ms-excel": ".xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "application/msword": ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
  "text/plain": ".txt"
};
const fileExtension = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");
  return dot >= 0 ? fileName.slice(dot).toLowerCase() : "";
};
export interface InputFileProps {
  /** Field name — also used as the hidden input name so the URL flows with FormData. */
  name: string;
  /** Label rendered above the drop zone. */
  label?: string;
  /** Long-form description rendered above the drop zone — plain string or rich content (e.g. a description with a link). */
  description?: React.ReactNode;
  /** Short hint rendered under the drop zone in non-error states. */
  helperText?: string | null;
  /** Current uploaded value — a URL string in single mode, or an array of URLs
   *  in multiple mode. */
  value?: string | string[];
  /** Called whenever the uploaded set resolves. Emits a URL string in single
   *  mode, or an array of URLs in multiple mode. */
  onChange?: (value: string | string[]) => void;
  /** Blur handler for form integration. */
  onBlur?: () => void;
  /** Whether the field is required. */
  required?: boolean;
  /** Server / validation error rendered below the drop zone. */
  error?: string;
  /** Max file size in MB. Defaults to 15. */
  maxSizeMb?: number;
  /** Allowed MIME types. Defaults to JPG/PNG/SVG/PDF. */
  acceptedMimeTypes?: string[];
  /** Allow attaching more than one file. */
  multiple?: boolean;
  /** Cap on the number of files in multiple mode. Ignored when single. */
  maxFiles?: number;
  /** Disables interaction. */
  disabled?: boolean;
  /** Identifies the parent form when minting the upload token. */
  formId?: string;
  /** Identifies the parent form wizard when minting the upload token. */
  formWizardId?: string;
  /** Optional className for the outer wrapper. */
  className?: string;
}

/** One file's lifecycle within the field. The list is the source of truth for
 *  both the rendered rows and the URLs emitted to the parent form. */
interface UploadEntry {
  id: string;
  name: string;
  sizeBytes: number;
  status: Status;
  url: string | null;
  error?: string;
  /** Retained so a failed upload can be retried. Absent for entries seeded
   *  from an existing value. */
  file?: File;
}
const formatBytes = (bytes: number): string => {
  if (!bytes) return "0 KB";
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};
const buildDerivedHelperText = (acceptedMimeTypes: string[], maxSizeMb: number) => {
  const exts = Array.from(new Set(acceptedMimeTypes.map(type => EXTENSION_BY_MIME[type] ?? type)));
  const head = exts.slice(0, -1).join(", ");
  const list = head ? `${head} or ${exts[exts.length - 1]}` : exts.join("");
  return `${list} (Max size ${maxSizeMb}MB)`;
};
const isValidFile = (file: File, acceptedMimeTypes: string[], maxSizeBytes: number): string | null => {
  // Match on MIME type, falling back to the file extension — browsers report
  // CSV and Office files inconsistently (or with an empty type).
  const ext = fileExtension(file.name);
  const mimeMatches = acceptedMimeTypes.includes(file.type);
  const extMatches = acceptedMimeTypes.some(type => FILE_EXTENSION_BY_MIME[type] === ext);
  if (!mimeMatches && !extMatches) {
    const exts = Array.from(new Set(acceptedMimeTypes.map(type => EXTENSION_BY_MIME[type] ?? type)));
    const head = exts.slice(0, -1).join(", ");
    const list = head ? `${head} or ${exts[exts.length - 1]}` : exts.join("");
    return list ? `File type not supported. Accepted: ${list}.` : "File type not supported.";
  }
  if (file.size > maxSizeBytes) {
    return `File is larger than the ${formatBytes(maxSizeBytes)} limit.`;
  }
  return null;
};

/** Derive a human-readable filename from a stored blob URL (used when seeding
 *  the field from an existing value, where the original File is gone). */
const fileNameFromUrl = (url: string): string => {
  try {
    const last = new URL(url).pathname.split("/").pop();
    return last ? decodeURIComponent(last) : url;
  } catch {
    return url;
  }
};

/** Normalise the incoming controlled value to a list of URLs. */
const urlsFromValue = (value: string | string[] | undefined): string[] => Array.isArray(value) ? value.filter(Boolean) : value ? [value] : [];

/** The value shape emitted to the parent: an array in multiple mode, otherwise
 *  the first URL (or empty string). */
const toEmittedValue = (urls: string[], multiple: boolean): string | string[] => multiple ? urls : urls[0] ?? "";
export const InputFile = React.forwardRef<HTMLInputElement, InputFileProps>(({
  name,
  label,
  description,
  helperText,
  value = "",
  onChange,
  onBlur,
  required,
  error,
  maxSizeMb = DEFAULT_MAX_SIZE_MB,
  acceptedMimeTypes = DEFAULT_ACCEPTED_MIME_TYPES,
  multiple = false,
  maxFiles,
  disabled,
  formId,
  formWizardId,
  className
}, ref) => {
  const generatedId = React.useId();
  const inputId = `${name}-${generatedId}`;
  const helperId = `${inputId}-helper`;
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const idCounter = React.useRef(0);
  const makeId = React.useCallback(() => `${inputId}-entry-${idCounter.current++}`, [inputId]);
  const [entries, setEntries] = React.useState<UploadEntry[]>(() =>
  // Seeded entries use index-based ids so this initializer never touches the
  // counter ref during render; runtime additions use `makeId` (a distinct
  // `-entry-` prefix, so the namespaces can't collide).
  urlsFromValue(value).map((url, index) => ({
    id: `${inputId}-seed-${index}`,
    name: fileNameFromUrl(url),
    sizeBytes: 0,
    status: "success" as const,
    url
  })));
  const [isDragging, setIsDragging] = React.useState(false);
  const maxSizeBytes = maxSizeMb * 1024 * 1024;
  const derivedHelper = buildDerivedHelperText(acceptedMimeTypes, maxSizeMb);
  // One hint inside the dropzone: the author's helper text when set, otherwise
  // the auto-derived types/size line. Avoids a second, possibly contradictory
  // hint elsewhere.
  const dropzoneHint = helperText && helperText.trim() ? helperText : derivedHelper;
  const hasError = Boolean(error);
  const successUrls = React.useMemo(() => entries.filter(entry => entry.status === "success" && entry.url).map(entry_0 => entry_0.url as string), [entries]);

  // Emit to the parent only when the resolved URL set actually changes,
  // seeded with the incoming value so we never emit on mount. The entry list
  // stays the source of truth (we never re-sync from `value`), avoiding
  // feedback loops with controlled forms.
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  });
  const lastEmittedRef = React.useRef<string>(JSON.stringify(toEmittedValue(urlsFromValue(value), multiple)));
  React.useEffect(() => {
    const out = toEmittedValue(successUrls, multiple);
    const key = JSON.stringify(out);
    if (key !== lastEmittedRef.current) {
      lastEmittedRef.current = key;
      onChangeRef.current?.(out);
    }
  }, [successUrls, multiple]);

  // Mirror of `entries` for handlers that need the latest list without being
  // re-created on every change (e.g. retry).
  const entriesRef = React.useRef(entries);
  React.useEffect(() => {
    entriesRef.current = entries;
  });
  const setRefs = React.useCallback((node: HTMLInputElement | null) => {
    fileInputRef.current = node;
    if (typeof ref === "function") ref(node);else if (ref) ref.current = node;
  }, [ref]);
  const updateEntry = React.useCallback((id: string, patch: Partial<UploadEntry>) => {
    setEntries(prev => prev.map(entry_1 => entry_1.id === id ? {
      ...entry_1,
      ...patch
    } : entry_1));
  }, []);
  const uploadOne = React.useCallback(async (id_0: string, file: File) => {
    const validationError = isValidFile(file, acceptedMimeTypes, maxSizeBytes);
    if (validationError) {
      updateEntry(id_0, {
        status: "error",
        error: validationError
      });
      return;
    }
    updateEntry(id_0, {
      status: "uploading",
      error: undefined
    });
    try {
      const blob = await upload(file.name, file, {
        access: "public",
        handleUploadUrl: "/api/upload",
        clientPayload: JSON.stringify({
          name,
          formId,
          formWizardId
        })
      });
      updateEntry(id_0, {
        status: "success",
        url: blob.url
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      updateEntry(id_0, {
        status: "error",
        error: message.includes("client token") ? "Upload was blocked by a security check. Please try again on a different network or browser (without a VPN or ad blocker), or contact us if the problem persists." : message || "Upload failed. Try again."
      });
    }
  }, [acceptedMimeTypes, formId, formWizardId, maxSizeBytes, name, updateEntry]);
  const addFiles = React.useCallback((fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList);
    if (!multiple) {
      const file_0 = incoming[0];
      if (!file_0) return;
      const id_1 = makeId();
      setEntries([{
        id: id_1,
        name: file_0.name,
        sizeBytes: file_0.size,
        status: "uploading",
        url: null,
        file: file_0
      }]);
      void uploadOne(id_1, file_0);
      return;
    }

    // Respect the per-field cap against files already attached.
    const remaining = typeof maxFiles === "number" ? Math.max(0, maxFiles - entriesRef.current.length) : Infinity;
    const accepted = incoming.slice(0, remaining);
    if (accepted.length === 0) return;
    const newEntries = accepted.map<UploadEntry>(file_1 => ({
      id: makeId(),
      name: file_1.name,
      sizeBytes: file_1.size,
      status: "uploading",
      url: null,
      file: file_1
    }));
    setEntries(prev_0 => [...prev_0, ...newEntries]);
    newEntries.forEach((entry_2, index_0) => {
      void uploadOne(entry_2.id, accepted[index_0] as File);
    });
  }, [makeId, maxFiles, multiple, uploadOne]);
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files);
    // Reset so re-selecting the same file fires another change event.
    event.target.value = "";
  };
  const handleDrop = (event_0: React.DragEvent<HTMLDivElement>) => {
    event_0.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    addFiles(event_0.dataTransfer.files);
  };
  const handleDragOver = (event_1: React.DragEvent<HTMLDivElement>) => {
    event_1.preventDefault();
    if (!disabled) setIsDragging(true);
  };
  const handleDragLeave = (event_2: React.DragEvent<HTMLDivElement>) => {
    event_2.preventDefault();
    setIsDragging(false);
  };
  const openFilePicker = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };
  const removeEntry = React.useCallback((id_2: string) => {
    setEntries(prev_1 => prev_1.filter(entry_3 => entry_3.id !== id_2));
  }, []);
  const retryEntry = React.useCallback((id_3: string) => {
    const entry_4 = entriesRef.current.find(item => item.id === id_3);
    if (entry_4?.file) void uploadOne(id_3, entry_4.file);else removeEntry(id_3);
  }, [removeEntry, uploadOne]);
  const accept = [...acceptedMimeTypes, ...acceptedMimeTypes.map(type => FILE_EXTENSION_BY_MIME[type]).filter((ext): ext is string => Boolean(ext))].join(",");
  const atCapacity = multiple ? typeof maxFiles === "number" && entries.length >= maxFiles : entries.length > 0;
  const showDropzone = !atCapacity;
  return <div className={cn("flex w-full flex-col gap-2", className)}>
        {Boolean(label) && <label htmlFor={inputId} className="typography-component-sm-regular desktop:typography-component-md-regular text-input-text-label">
            {label}
            <RequiredMark required={required} />
          </label>}
        {Boolean(description) && <p className="typography-body-sm-regular desktop:typography-body-md-regular text-text-secondary">
            {description}
          </p>}

        {entries.length > 0 && <div className="flex w-full flex-col gap-2">
            {entries.map(entry_5 => {
        const sizeText = entry_5.sizeBytes > 0 ? formatBytes(entry_5.sizeBytes) : "";
        const statusText = entry_5.status === "uploading" ? "Uploading…" : entry_5.status === "success" ? "Completed" : "Upload failed";
        const metaLine = sizeText ? `${sizeText} - ${statusText}` : statusText;
        return <div key={entry_5.id} data-testid="file-row" className={cn("flex w-full items-center gap-3 rounded-lg border border-solid px-4 py-3", entry_5.status === "uploading" && "border-input-border bg-input-background", entry_5.status === "success" && "border-border-default bg-surface-base-interactive", entry_5.status === "error" && "border-input-border-error bg-surface-feedback-error")}>
                  <span className={cn("flex size-8 shrink-0 items-center justify-center rounded-md", entry_5.status === "uploading" && "bg-surface-base-secondary", entry_5.status === "success" && "bg-surface-feedback-success", entry_5.status === "error" && "bg-surface-feedback-error")} aria-hidden="true">
                    {entry_5.status === "uploading" && <FileText className="size-4 text-text-secondary" />}
                    {entry_5.status === "success" && <Check className="text-text-feedback-success size-4" strokeWidth={2.5} />}
                    {entry_5.status === "error" && <X className="text-text-feedback-error size-4" strokeWidth={2.5} />}
                  </span>

                  <div className="flex min-w-0 flex-1 flex-col">
                    <p className="typography-component-sm-medium text-text-primary truncate">
                      {entry_5.name}
                    </p>
                    <p className="typography-body-sm-regular desktop:typography-body-md-regular text-text-secondary">
                      {metaLine}
                    </p>
                    {entry_5.status === "error" && entry_5.error && <p className="typography-body-sm-regular desktop:typography-body-md-regular text-input-text-error">
                        {entry_5.error}
                      </p>}
                    {entry_5.status === "uploading" && <div className="bg-surface-base-secondary mt-2 h-1 w-full overflow-hidden rounded-full" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-busy="true">
                        <div className="bg-border-focus h-full w-full animate-pulse" />
                      </div>}
                  </div>

                  {entry_5.status === "success" && <span className="typography-component-sm-medium desktop:typography-component-md-medium text-text-primary shrink-0">
                      Success
                    </span>}
                  {entry_5.status === "error" && <button type="button" onClick={() => retryEntry(entry_5.id)} className="typography-component-sm-medium desktop:typography-component-md-medium text-text-primary shrink-0 underline">
                      Try again
                    </button>}

                  <button type="button" onClick={() => removeEntry(entry_5.id)} aria-label={`Remove ${entry_5.name}`} className="text-text-secondary hover:text-text-primary shrink-0">
                    <Trash2 className="size-4" aria-hidden="true" />
                  </button>
                </div>;
      })}
          </div>}

        {showDropzone && <div data-testid="dropzone" role="button" tabIndex={disabled ? -1 : 0} aria-disabled={disabled || undefined} aria-describedby={helperId} onClick={openFilePicker} onKeyDown={event_3 => {
      if (event_3.key === "Enter" || event_3.key === " ") {
        event_3.preventDefault();
        openFilePicker();
      }
    }} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onBlur={onBlur} className={cn("flex w-full cursor-pointer items-center gap-3 rounded-lg border border-dashed px-4 py-3 transition-colors", "border-input-border bg-surface-base-secondary", "hover:border-input-border-hover", "focus:border-input-border-focus focus:outline-none focus:ring-1 focus:ring-input-border-focus", isDragging && "border-input-border-focus bg-surface-base-interactive", hasError && "border-input-border-error bg-surface-feedback-error", disabled && "pointer-events-none opacity-50")}>
            <span className="bg-surface-base-primary flex size-8 shrink-0 items-center justify-center rounded-md" aria-hidden="true">
              <UploadCloud className={cn("size-4", hasError ? "text-text-feedback-error" : "text-text-secondary")} />
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              <p className="typography-body-sm-regular text-text-primary">
                <span className="font-medium underline">Click to upload</span>{" "}
                or drag and drop
              </p>
              <p id={helperId} className="typography-body-sm-regular desktop:typography-body-md-regular text-text-secondary">
                {dropzoneHint}
              </p>
            </div>
          </div>}

        {error && <p role="alert" className="typography-component-sm-regular desktop:typography-component-md-regular text-input-text-error">
            {error}
          </p>}

        {/* Hidden native file input — keyboard / pointer fallback for the dropzone. */}
        <input ref={setRefs} id={inputId} type="file" accept={accept} multiple={multiple || undefined} onChange={handleFileInputChange} onBlur={onBlur} disabled={disabled} className="sr-only" tabIndex={-1} aria-hidden="true" />

        {/* The blob URL(s) — flow with the surrounding FormData submission. In
            multiple mode each URL is a separate input under the same name so
            `decode-formdata` reconstructs them as an array. */}
        {multiple ? successUrls.map((url_0, index_1) => <input key={`${name}-${index_1}`} type="hidden" name={name} value={url_0} />) : <input type="hidden" name={name} value={successUrls[0] ?? ""} />}
      </div>;
});
InputFile.displayName = "InputFile";